import {useEffect, useState} from "react";
import {useNavigate, useParams, useLocation} from "react-router-dom";
import {useSelector} from "react-redux";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import VLFTextField from "@/components/Input/TextField";
import VoiceInput from "@/components/VoiceInput";
import VLFDatePicker from "@/components/Input/DatePicker";
import SmartRadarChart from "@/components/SmartRadarChart";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";

import {PlayerAPI} from "@/api/player";
import {PlayerValuationAPI} from "@/api/player_valuation";

import {ERROR_OPERATION, SUCCESSFUL_OPERATION} from "@/utils/constants";
import {
	SMART_PARAMS,
	SMART_CATEGORIES,
	valuesToApiFields,
	apiFieldsToValues,
} from "@/config/smartValuationConfig";

import "./index.scss";

function HandleSmartValuationPage(props) {
	const {edit, handleNotification} = props;

	const navigate = useNavigate();
	const {id} = useParams();
	const location = useLocation();
	const searchParams = new URLSearchParams(location.search);
	const playerIdFromQuery = searchParams.get("playerId");

	const seasonId = useSelector((state) => state?.season?.id);

	const [loading, setLoading] = useState(false);
	const [playerData, setPlayerData] = useState(null);
	const [values, setValues] = useState({});
	const [meta, setMeta] = useState({
		valuationDate: moment().format("YYYY-MM-DD"),
		valuationAuthor: "",
		valuationText: "",
	});

	// ── Load player ───────────────────────────────────────────────────────────

	useEffect(() => {
		if (playerIdFromQuery) {
			PlayerAPI.getById(playerIdFromQuery)
				.then(setPlayerData)
				.catch(() => {});
		}
	}, [playerIdFromQuery]);

	// ── Load existing valuation (edit mode) ───────────────────────────────────

	useEffect(() => {
		if (edit && id) {
			setLoading(true);
			PlayerValuationAPI.getById(id)
				.then((data) => {
					setMeta({
						valuationDate: data.valuationDate,
						valuationAuthor: data.valuationAuthor || "",
						valuationText: data.valuationText || "",
					});
					setValues(apiFieldsToValues(data));
					if (data.playerId && !playerData) {
						PlayerAPI.getById(data.playerId)
							.then(setPlayerData)
							.catch(() => {});
					}
				})
				.catch(() => handleNotification(ERROR_OPERATION, "error"))
				.finally(() => setLoading(false));
		}
	}, [edit, id]); // eslint-disable-line react-hooks/exhaustive-deps

	// ── Save ──────────────────────────────────────────────────────────────────

	const handleSave = async () => {
		setLoading(true);
		try {
			const body = {
				playerId: playerIdFromQuery || playerData?.id,
				valuationType: 7,
				valuationDate: moment(meta.valuationDate).format("YYYY-MM-DD"),
				valuationAuthor: meta.valuationAuthor,
				valuationText: meta.valuationText,
				seasonId,
				...valuesToApiFields(values),
			};

			if (edit && id) {
				await PlayerValuationAPI.update(id, body);
			} else {
				await PlayerValuationAPI.create(body);
			}

			handleNotification(SUCCESSFUL_OPERATION, "success");
			navigate("/players/" + (playerIdFromQuery || playerData?.id));
		} catch {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	// ── Update single value ───────────────────────────────────────────────────

	const setValue = (paramId, val) => {
		const num = val === "" ? null : Math.min(100, Math.max(0, parseInt(val) || 0));
		setValues((prev) => ({...prev, [paramId]: num}));
	};

	// ── Render ────────────────────────────────────────────────────────────────

	const mainActions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: () => navigate(-1),
			color: "info",
		},
		{
			icon: <SaveIcon />,
			title: "Salva",
			onClick: handleSave,
		},
	];

	const paramsByCategory = SMART_CATEGORIES.map((cat) => ({
		...cat,
		params: SMART_PARAMS.filter((p) => p.category === cat.id),
	}));

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs
				elements={[{label: "Valutazioni", path: "/players/" + playerIdFromQuery}]}
				current={edit ? "Modifica SMART" : "Nuova valutazione SMART"}
				actions={<MainActions actions={mainActions} />}
			/>

			<div className="hsv">
				<div className="hsv__layout">
					{/* ── Left: Form ──────────────────────────────── */}
					<div className="hsv__form">
						{/* Player info */}
						{playerData && (
							<div className="hsv__player-card">
								<div className="hsv__player-name">
									{playerData.firstName} {playerData.lastName}
								</div>
								<div className="hsv__player-meta">
									{playerData.primaryRole || ""} · {playerData.category || ""}
								</div>
							</div>
						)}

						{/* Meta fields */}
						<VLFCard
							title="Informazioni"
							openable={false}
							content={
								<div className="row">
									<div className="col-sm-6">
										<VLFDatePicker
											label="Data valutazione"
											value={meta.valuationDate}
											onChange={(v) => setMeta({...meta, valuationDate: v})}
											size="small"
										/>
									</div>
									<div className="col-sm-6">
										<VLFTextField
											name="author"
											value={meta.valuationAuthor}
											onChange={(e) => setMeta({...meta, valuationAuthor: e.target.value})}
											label="Autore"
											size="small"
										/>
									</div>
								</div>
							}
						/>

						{/* Parameters by category */}
						{paramsByCategory.map((cat) => (
							<VLFCard
								key={cat.id}
								title={cat.label}
								openable={false}
								content={
									<div className="hsv__params-grid">
										{cat.params.map((param) => (
											<div key={param.id} className="hsv__param">
												<div className="hsv__param-header">
													<span className="hsv__param-label">{param.label}</span>
													<span className="hsv__param-value" style={{color: cat.color}}>
														{values[param.id] ?? "—"}
													</span>
												</div>
												<input
													type="range"
													min="0"
													max="100"
													value={values[param.id] ?? 50}
													onChange={(e) => setValue(param.id, e.target.value)}
													className="hsv__param-slider"
													style={{"--slider-color": cat.color}}
												/>
											</div>
										))}
									</div>
								}
							/>
						))}

						{/* Notes */}
						<VLFCard
							title="Note"
							openable={false}
							content={
								<VoiceInput
									value={meta.valuationText}
									onChange={(val) => setMeta({...meta, valuationText: val})}
									label="Note generali"
									placeholder="Scrivi o detta la valutazione..."
									rows={3}
								/>
							}
						/>
					</div>

					{/* ── Right: Preview ──────────────────────────── */}
					<div className="hsv__preview">
						<div className="hsv__preview-sticky">
							<VLFCard
								title="Preview"
								openable={false}
								content={
									<SmartRadarChart
										values={values}
										playerName={playerData ? `${playerData.firstName} ${playerData.lastName}` : ""}
									/>
								}
							/>
						</div>
					</div>
				</div>
			</div>
		</Page>
	);
}

export default HandleSmartValuationPage;
